import { Component, OnInit } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { AuthService } from '../services/auth.service';
import { environment } from '../../environments/environment';

@Component({
  selector: 'app-edit-account',
  templateUrl: './edit-account.component.html',
  styleUrls: ['./edit-account.component.css']
})
export class EditAccountComponent implements OnInit {
  accountForm!: FormGroup;
  isSaving = false;
  saveSuccess = false;
  saveError: string | null = null;
  isProvider = false;
  username = '';

  constructor(
    private fb: FormBuilder,
    private authService: AuthService,
    private http: HttpClient,
    private router: Router
  ) {}

  ngOnInit(): void {
    const user = this.authService.getCurrentUser();
    if (!user) { this.router.navigate(['/auth']); return; }

    this.isProvider = user.role === 'provider';
    this.username = user.username;

    const sa = (user as any).serviceArea;
    const saText = Array.isArray(sa) ? sa.join(', ') : (sa || '');

    this.accountForm = this.fb.group({
      firstName:       [user.firstName   || '', Validators.required],
      lastName:        [user.lastName    || '', Validators.required],
      email:           [user.email       || '', [Validators.required, Validators.email]],
      phone:           [(user as any).phone || ''],
      address:         [user.address     || ''],
      serviceArea:     [saText],
      newPassword:     [''],
      confirmPassword: [''],
    }, { validators: this.passwordMatchValidator });
  }

  private passwordMatchValidator(group: AbstractControl): ValidationErrors | null {
    const pw  = group.get('newPassword')?.value;
    const cpw = group.get('confirmPassword')?.value;
    if (pw && pw !== cpw) return { passwordMismatch: true };
    return null;
  }

  get passwordMismatch(): boolean {
    return !!(this.accountForm?.hasError('passwordMismatch') &&
              this.accountForm?.get('confirmPassword')?.touched);
  }

  onSubmit(): void {
    if (this.accountForm.invalid) return;
    const user = this.authService.getCurrentUser();
    if (!user) return;

    this.isSaving = true;
    this.saveError = null;
    this.saveSuccess = false;

    const fv = this.accountForm.value;
    const payload: any = {
      id:          user.id,
      username:    user.username,
      userGuid:    user.userGuid,
      role:        user.role,
      userBadge:   (user as any).userBadge  || '',
      balance:     (user as any).balance    || 0,
      referredBy:  (user as any).referredBy || '',
      firstName:   fv.firstName,
      lastName:    fv.lastName,
      email:       fv.email,
      phone:       fv.phone      || null,
      address:     fv.address    || null,
      serviceArea: fv.serviceArea || null,
    };

    if (fv.newPassword) payload.password = fv.newPassword;

    this.http.put<any>(`${environment.apiUrl}Users/${user.id}`, payload).subscribe({
      next: (updated) => {
        const refreshed = { ...user, ...updated, role: user.role, password: undefined };
        this.authService.setCurrentUser(refreshed);
        this.saveSuccess = true;
        this.isSaving = false;
        this.accountForm.patchValue({ newPassword: '', confirmPassword: '' });
        this.accountForm.get('newPassword')?.markAsUntouched();
        this.accountForm.get('confirmPassword')?.markAsUntouched();
      },
      error: () => {
        this.saveError = 'Failed to save changes. Please try again.';
        this.isSaving = false;
      }
    });
  }

  goBack(): void {
    const user = this.authService.getCurrentUser();
    this.router.navigate([user?.role === 'provider' ? '/provider-dashboard' : '/dashboard']);
  }
}
